import { useEffect, useMemo, useState } from 'react';
import { useOpenUnsavedChangesModal, useOverlayContext, usePrevious, useStateInRef } from 'seagull';
import IntroVideoStep from "./IntroVideoStep/IntroVideoStep";
import { ErrorText } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useEventReporter } from "../../event-reporter";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useOnMount } from "../../hooks/useOnMount";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { handleRequest } from "../../infra/utils";
import { relationshipModel, serviceModel } from "../../models";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import StripeElementsProvider from "../../providers/StripeElementsProvider";
import { selectors } from "../../store/state-selectors";
import { ClientFlowStructure } from "../components/ClientFlowStructure";
import { ResponsiveProvider } from "../contexts";
import AccountantAccessStep from "./AccountantAccessStep/AccountantAccessStep";
import ApproveTermsStep from "./ApproveTermsStep/ApproveTermsStep";
import { BrandingActionsProvider } from "./BrandingActionsContext";
import styles from "./ClientOnboarding.module.scss";
import { COBStep, getInitialSelectedBundleId, getInitialSelectedOptionalServices, getInitialSelectedPaymentMethod, getIsAllowedToSIgn, getReachableSteps, getStepName, PreviewState, Reason, termsOfServiceStepIndex } from "./ClientOnboarding.utils";
import { useCobBranding } from "./CobBrandingContext";
import { CobConfigProvider } from "./CobConfigContext";
import { useCobStorage } from "./CobStorageContext";
import ConnectPaymentMethodStep from "./ConnectPaymentMethodStep/ConnectPaymentMethodStep";
import PackagesStep from "./PackagesStep/PackagesStep";
import { PreviewBanner } from "./PreviewBanner";
import PendingAdditionalSignaturesWithAnimations from "./RelationshipSignedStep/PendingAdditionalSignaturesWithAnimations";
import ReviewServicesStep from "./ReviewServicesStep/ReviewServicesStep";
import { TermsOfServiceStep } from "./TermsOfServiceStep/TermsOfServiceStep";
import WelcomeStep from "./WelcomeStep/WelcomeStep";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export function ClientOnboardingInner(props) {
  const {
    relationship,
    currentStep,
    previewState,
    disableMobile,
    dismissPreviewModal,
    onPreviewStateChange,
    setCurrentStep,
    refresh
  } = props;
  const [localPreviewState, setLocalPreviewState] = useState(previewState);
  const isPreview = !!localPreviewState;
  const userId = useStore(selectors.user.id);
  const isReviewer = relationshipModel.isReviewer(relationship, userId);
  const featureFlags = useFeatureFlags();
  const isMemberClient = useStore(s => selectors.user.isMemberUser(s) && selectors.user.isClient(s));
  const {
    logBI,
    api
  } = useStoreActions();
  const {
    reportToGtm,
    reportToAppcues,
    reportToHubspot,
    reportToHeap
  } = useEventReporter();
  const {
    getFromStorage,
    setToStorage,
    clearStorage
  } = useCobStorage(relationship);
  const reachableSteps = getReachableSteps(relationship, Boolean(featureFlags.MULTIPLE_LEGAL_TERMS_DOCUMENTS));
  const overlayContext = useOverlayContext();
  const role = useStore(selectors.user.role);
  const shouldSignStatement = getIsAllowedToSIgn({
    relationship,
    userId,
    role
  });
  const termsOfServices = useMemo(() => {
    var _relationship$legalTe;
    return (_relationship$legalTe = relationship.legalTermsDocuments) === null || _relationship$legalTe === void 0 ? void 0 : _relationship$legalTe.slice(0, 4);
  }, [relationship.legalTermsDocuments]);
  const [termsOfServiceStatements, setTermsOfServiceStatements] = useState(termsOfServices.map(termsOfService => shouldSignStatement || isPreview ? !(termsOfService.termsOfService.type === 'explicitApproval' && termsOfService.termsOfService.explicitApproval.statement) : true));
  const [prevVisitedSteps, setPrevVisitedSteps] = useState(getFromStorage('prev_visited_steps') || []);
  const activeStepIndex = Math.max(reachableSteps.findIndex(s => s === currentStep), 0);
  const lastActiveStepIndex = usePrevious(activeStepIndex);
  const getStateInRef = useStateInRef({
    prevVisitedSteps,
    prevActiveStepIndex: lastActiveStepIndex,
    setToStorage
  });
  const {
    isDirty,
    revertToInitial
  } = useCobBranding();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const [approveAgreementStatus, setApproveAgreementStatus] = useState(null);
  const [disabledReason, setDisabledReason] = useState();
  const [selectedPayment, setSelectedPayment] = useState(() => getInitialSelectedPaymentMethod(getFromStorage, relationship, userId));
  const [selectedBundleID, setSelectedBundleID] = useState(() => getInitialSelectedBundleId(getFromStorage, relationship, userId));
  const [selectedOptionalServices, _setSelectedOptionalServices] = useState(() => getInitialSelectedOptionalServices({
    selectedBundleID,
    getFromStorage,
    isPreview,
    relationship,
    setToStorage,
    userId
  }));
  const previewStatus = isPreview ? dismissPreviewModal ? {
    inModal: true,
    onDismissPreviewModal: dismissPreviewModal
  } : {
    inModal: false
  } : undefined;
  async function toggleBrandSetup() {
    if (localPreviewState === PreviewState.SetupBrand) {
      if (isDirty) {
        const confirmed = await openUnsavedChangesModal({
          testid: 'client-preview-unsaved-popup',
          title: featureFlags.SET_UP_BRAND_CLIENT_PREVIEW ? 'Unsaved brand changes' : undefined
        });
        if (confirmed) {
          revertToInitial();
          setLocalPreviewState(previewState);
          onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
        }
      } else {
        setLocalPreviewState(previewState);
        onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(previewState);
      }
    } else {
      setLocalPreviewState(PreviewState.SetupBrand);
      onPreviewStateChange === null || onPreviewStateChange === void 0 || onPreviewStateChange(PreviewState.SetupBrand);
    }
  }
  const setSelectedOptionalServices = val => {
    _setSelectedOptionalServices(val);
    setToStorage('selected_optional_services', val);
  };
  const completedSteps = prevVisitedSteps.filter(stepIdx => {
    const step = reachableSteps[stepIdx];
    switch (step) {
      case COBStep.selectAPackage:
        return selectedBundleID != null;
      case COBStep.paymentMethod:
        return !!selectedPayment;
      case COBStep.accountantAccess:
        {
          const checked = getFromStorage('request_accountant_access');
          return relationship.requestAccountantAccess !== RequestAccountantAccess.Mandatory || checked;
        }
      case COBStep.termsOfService1:
      case COBStep.termsOfService2:
      case COBStep.termsOfService3:
      case COBStep.termsOfService4:
        return termsOfServiceStatements[termsOfServiceStepIndex[step]];
      default:
        return true;
    }
  });
  useEffect(() => {
    const {
      prevActiveStepIndex,
      prevVisitedSteps,
      setToStorage
    } = getStateInRef();
    if (prevActiveStepIndex != null && !prevVisitedSteps.includes(prevActiveStepIndex)) {
      setPrevVisitedSteps(prevVisited => {
        const newPrevVisitedSteps = [...prevVisited, prevActiveStepIndex];
        setToStorage('prev_visited_steps', newPrevVisitedSteps);
        return newPrevVisitedSteps;
      });
    }
  }, [activeStepIndex]);
  useEffect(() => {
    if (currentStep && currentStep !== COBStep.welcome) {
      if (currentStep === COBStep.allSet) {
        clearStorage();
      } else {
        setToStorage('last_step', currentStep);
      }
    }
  }, [clearStorage, currentStep, setToStorage]);
  useOnMount(() => {
    if (!isPreview) {
      if (relationship.isSample) {
        logBI(11903);
        reportToGtm('notification-open', 'vendor-opened-test-agreement');
        reportToAppcues('notification-open', 'vendor-opened-test-agreement');
        reportToHeap('testAgreement', 'load', 'vendor-opened-test-agreement');
      } else {
        logBI(10824);
        reportToHeap('clientOb', 'load', 'agreement', featureFlags);
      }
      if (window.heap != null) window.heap.addUserProperties(featureFlags);
    }
    if (relationshipModel.hasUserSigned(relationship, userId)) setCurrentStep(COBStep.allSet);
  });
  function changeSelectedPayment(selectedPayment) {
    setSelectedPayment(selectedPayment);
    if (selectedPayment) setToStorage('selected_payment_method', selectedPayment);
  }
  function navigateFrom(currentStep) {
    const currentIdx = reachableSteps.indexOf(currentStep);
    return {
      goNext: () => setCurrentStep(reachableSteps[currentIdx + 1]),
      goBack: () => setCurrentStep(reachableSteps[currentIdx - 1])
    };
  }
  async function handleAgreementSubmission(fundingSourceId, selectedBundleId) {
    const loaderName = 'lastStep';
    overlayContext.startLoader(loaderName);
    const payload = {
      fundingSourceId,
      selectedBundleId,
      selectedOptionalServices: getFromStorage('selected_optional_services'),
      requestAccountantAccessApproved: requestAccountantAccessModel.wasRequested(relationship.requestAccountantAccess)
    };
    const request = relationshipModel.isSignatory(relationship, userId) ? api.relationship.signatoryApprove(relationship, payload) : api.relationship.approve(relationship, payload);
    const {
      success,
      error
    } = await handleRequest(request);
    overlayContext.stopLoader(loaderName);
    if (!success) {
      if (error.hasServerError(ServerError.UserAlreadySigned)) {
        overlayContext.showSuccessToast('You have already signed the agreement');
      } else {
        const errorText = error.hasServerError(ServerError.MismatchingRelationshipVersion) ? ErrorText.MismatchingRelationshipVersion : error.hasServerError(ServerError.InvalidRelationshipStatus) ? 'The proposal was canceled' : null;
        errorText && overlayContext.showErrorToast(errorText);
        throw error;
      }
    }
  }
  useEffect(() => {
    if (!relationship.isSample && reachableSteps.includes(COBStep.paymentMethod) && !selectedPayment) {
      setDisabledReason(Reason.PaymentMethod);
      return;
    }
    if (reachableSteps.includes(COBStep.selectAPackage) && !selectedBundleID) {
      setDisabledReason(Reason.SelectAPackage);
      return;
    }
    if (termsOfServiceStatements.includes(false)) {
      setDisabledReason(Reason.ApproveTermsOfServices);
      return;
    }
    if (!relationshipModel.actualServices(relationship, selectedBundleID).some(service => serviceModel.isMandatory(service.participation) || selectedOptionalServices.includes(service.id))) {
      setDisabledReason(Reason.ReviewServices);
      return;
    }
    setDisabledReason(undefined);
  }, [selectedPayment, relationship, selectedBundleID, selectedOptionalServices, reachableSteps, termsOfServiceStatements]);
  async function approveAgreement() {
    if (relationship.isSample) {
      logBI(11904);
    } else {
      logBI(10558);
    }
    try {
      const navigateToPendingRelationshipPage = () => navigateFrom(currentStep).goNext();
      setApproveAgreementStatus('loading');
      await handleAgreementSubmission(selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId, selectedBundleID);
      if (relationship.isSample) {
        reportToGtm('button-click', 'vendor-approved-test-agreement');
        reportToAppcues('button-click', 'vendor-approved-test-agreement');
        reportToHeap('testAgreement', 'click', 'vendor-approved-test-agreement');
        reportToHubspot('vendor_approved_test_agreement');
      } else {
        const paymentType = selectedPayment && selectedPayment.type === 'bankAccount' && (selectedPayment.status === 'new' || selectedPayment.status === 'validated') ? 'microDeposit' : selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.type;
        reportToGtm('button-click', 'client-approved-agreement', {
          paymentType
        });
        reportToAppcues('button-click', 'client-approved-agreement');
        reportToHubspot('client-approved-agreement', {
          paymentType
        });
        reportToHeap('clientOb', 'click', 'client-approved-agreement', {
          paymentType
        });
      }
      setApproveAgreementStatus(null);
      const {
        relationship: updatedRelationship
      } = await refresh();
      relationshipModel.isPending(updatedRelationship) && navigateToPendingRelationshipPage();
    } catch (e) {
      setApproveAgreementStatus('error');
      if (!(e instanceof ErrorResponse) || !e.hasServerError(ServerError.InvalidRelationshipStatus)) overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'approve-agreement-error-toast');
    }
  }
  function dynamicStepsRenderer(step) {
    switch (step) {
      case COBStep.welcome:
        {
          const {
            goNext
          } = navigateFrom(COBStep.welcome);
          return /*#__PURE__*/_jsx(WelcomeStep, {
            onDone: goNext
          });
        }
      case COBStep.selectAPackage:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.selectAPackage);
          return /*#__PURE__*/_jsx(PackagesStep, {
            selectedBundleID: selectedBundleID,
            onSelectBundleID: id => {
              setToStorage('selected_bundle_id', id);
              setSelectedBundleID(id);
              setSelectedOptionalServices(relationshipModel.includedByDefaultOptionalServices(relationship, id));
              goNext();
            },
            onClickNext: goNext,
            onClickBack: goBack
          });
        }
      case COBStep.reviewServices:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.reviewServices);
          return /*#__PURE__*/_jsx(ReviewServicesStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            setSelectedOptionalServices: setSelectedOptionalServices,
            onDone: goNext,
            onBack: goBack
          });
        }
      case COBStep.termsOfService1:
      case COBStep.termsOfService2:
      case COBStep.termsOfService3:
      case COBStep.termsOfService4:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(step);
          const index = termsOfServiceStepIndex[step];
          const currentTermsOfService = termsOfServices === null || termsOfServices === void 0 ? void 0 : termsOfServices[index];
          return /*#__PURE__*/_jsx(TermsOfServiceStep, {
            relationship: relationship,
            termsOfService: currentTermsOfService,
            onBack: goBack,
            onNext: goNext,
            isStatementAccepted: termsOfServiceStatements[index],
            shouldSignStatement: shouldSignStatement,
            isPeview: isPreview,
            onStatementChange: isAccepted => {
              setTermsOfServiceStatements(prev => {
                const newStatements = [...prev];
                newStatements[termsOfServiceStepIndex[step]] = isAccepted;
                return newStatements;
              });
            }
          });
        }
      case COBStep.approveTerms:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.approveTerms);
          const isLastStep = reachableSteps[reachableSteps.length - 2] === step;
          return /*#__PURE__*/_jsx(ApproveTermsStep, {
            selectedBundleID: selectedBundleID,
            selectedOptionalServices: selectedOptionalServices,
            previewStatus: previewStatus,
            onDone: goNext,
            onBack: goBack,
            disabledReason: isReviewer && isLastStep ? Reason.ReviewerApprove : !isPreview && isLastStep ? disabledReason : undefined,
            approveAgreement: approveAgreement,
            isLastStep: isLastStep,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.accountantAccess:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.accountantAccess);
          return /*#__PURE__*/_jsx(AccountantAccessStep, {
            onBack: goBack,
            onDone: goNext
          });
        }
      case COBStep.paymentMethod:
        {
          const {
            goBack
          } = navigateFrom(COBStep.paymentMethod);
          return /*#__PURE__*/_jsx(ConnectPaymentMethodStep, {
            selectedPaymentId: selectedPayment === null || selectedPayment === void 0 ? void 0 : selectedPayment.fundingSourceId,
            isMemberClient: isMemberClient,
            previewStatus: previewStatus,
            onSelectPaymentMethod: changeSelectedPayment,
            onDone: approveAgreement,
            onBack: goBack,
            disabledReason: isReviewer ? Reason.ReviewerApprove : isPreview ? undefined : disabledReason,
            approveAgreementStatus: approveAgreementStatus
          });
        }
      case COBStep.allSet:
        return /*#__PURE__*/_jsx(PendingAdditionalSignaturesWithAnimations, {});
      case COBStep.introVideo:
        {
          const {
            goNext,
            goBack
          } = navigateFrom(COBStep.introVideo);
          return /*#__PURE__*/_jsx(IntroVideoStep, {
            onDone: goNext,
            onBack: goBack
          });
        }
      default:
        setCurrentStep(COBStep.welcome);
        return /*#__PURE__*/_jsx(_Fragment, {});
    }
  }
  return /*#__PURE__*/_jsxs("div", {
    "data-testid": "client-onboarding",
    className: styles.root,
    children: [isPreview && /*#__PURE__*/_jsx(PreviewBanner, {
      previewState: previewState,
      localPreviewState: localPreviewState,
      onToggleBrandSetup: featureFlags.SET_UP_BRAND_CLIENT_PREVIEW ? toggleBrandSetup : undefined,
      onDismiss: featureFlags.SET_UP_BRAND_CLIENT_PREVIEW ? dismissPreviewModal : undefined,
      showBrandSetupButton: featureFlags.SET_UP_BRAND_CLIENT_PREVIEW
    }), /*#__PURE__*/_jsx("div", {
      className: styles.main,
      children: /*#__PURE__*/_jsx(ResponsiveProvider, {
        disableMobile: disableMobile,
        children: /*#__PURE__*/_jsx(CobConfigProvider, {
          relationship: relationship,
          previewState: localPreviewState,
          originalPreviewState: previewState,
          isPreviewModal: !!(previewStatus !== null && previewStatus !== void 0 && previewStatus.inModal),
          children: /*#__PURE__*/_jsx(BrandingActionsProvider, {
            onToggleBrandSetup: toggleBrandSetup,
            children: /*#__PURE__*/_jsx(StripeElementsProvider, {
              children: /*#__PURE__*/_jsx(ClientFlowStructure, {
                hideBack: currentStep === COBStep.allSet,
                hideStepper: currentStep === COBStep.allSet,
                activeStepIndex: activeStepIndex,
                stepperSteps: reachableSteps.slice(0, -1).map(step => getStepName(step, relationship, Boolean(featureFlags.MULTIPLE_LEGAL_TERMS_DOCUMENTS))),
                completedSteps: completedSteps,
                onStepClick: i => reachableSteps[i] && setCurrentStep(reachableSteps[i]),
                "data-testid": "clientob-pane",
                activeSteps: [...new Set([...prevVisitedSteps, activeStepIndex])],
                children: dynamicStepsRenderer(currentStep)
              })
            })
          })
        })
      })
    })]
  });
}